import { FC, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Autocomplete, Stack, TextField } from '@mui/material'

import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { FeatureModule, InternalRole } from '../../types/Plans'
import { getModuleNameError, ModuleFormValues, sortRoleNames, toModuleFormValues } from '../helpers/planHelpers'

type ModuleEditDialogProps = {
  open: boolean
  featureModule?: FeatureModule
  modules: FeatureModule[]
  roles: InternalRole[]
  isSaving?: boolean
  onClose: () => void
  onSave: (values: ModuleFormValues) => void
}

export const ModuleEditDialog: FC<ModuleEditDialogProps> = ({ open, featureModule, modules, roles, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState } = useForm<ModuleFormValues>({ mode: 'onChange', defaultValues: toModuleFormValues(featureModule) })

  useEffect(() => {
    if (open) reset(toModuleFormValues(featureModule))
  }, [open, featureModule, reset])

  const roleOptions = useMemo(() => sortRoleNames(roles.map(({ name }) => name)), [roles])
  const submit = handleSubmit(onSave)
  const canSave = formState.isValid && (!featureModule || formState.isDirty) && !isSaving

  return (
    <InternalFormDialog
      open={open}
      title={featureModule ? t('internal-csm:plans_edit_module') : t('internal-csm:plans_new_module')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={!canSave}
    >
      <Stack spacing={2} pt={1}>
        <Controller
          name="name"
          control={control}
          rules={{
            validate: (name) => {
              const error = getModuleNameError(name, modules, featureModule?.id)
              return error ? t(`internal-csm:plans_name_${error}`) : true
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              size="small"
              label={t('internal-csm:plans_module_name')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              required
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => <TextField {...field} size="small" label={t('internal-csm:plans_description')} />}
        />
        <Controller
          name="roles"
          control={control}
          render={({ field }) => (
            <Autocomplete
              multiple
              filterSelectedOptions
              options={roleOptions}
              value={field.value}
              onChange={(_, value) => field.onChange(value)}
              renderInput={(params) => <TextField {...params} label={t('internal-csm:plans_roles')} placeholder={t('internal-csm:plans_add_role')} />}
            />
          )}
        />
      </Stack>
    </InternalFormDialog>
  )
}
